import { supabase } from '../supabase'
import type { Volunteer, VolunteerHour } from '../types'

export const getVolunteers = async (status?: string) => {
  let q = supabase.from('volunteers')
    .select('*, counties(name)').is('deleted_at', null).order('last_name')
  if (status) q = q.eq('status', status)
  const { data, error } = await q
  if (error) throw error
  return data as Volunteer[]
}

export const getVolunteer = async (id: string) => {
  const { data, error } = await supabase.from('volunteers')
    .select('*, counties(name)').eq('id', id).is('deleted_at', null).single()
  if (error) throw error
  return data as Volunteer
}

export const getVolunteerByProfile = async (profileId: string) => {
  const { data, error } = await supabase.from('volunteers')
    .select('*').eq('profile_id', profileId).is('deleted_at', null).maybeSingle()
  if (error) throw error
  return data as Volunteer | null
}

export const createVolunteer = async (payload: Partial<Volunteer>) => {
  const { data, error } = await supabase.from('volunteers').insert(payload).select().single()
  if (error) throw error
  return data as Volunteer
}

export const updateVolunteer = async (id: string, payload: Partial<Volunteer>) => {
  const { data, error } = await supabase.from('volunteers')
    .update(payload).eq('id', id).select().single()
  if (error) throw error
  return data as Volunteer
}

// ── Hours ─────────────────────────────────────────────────────
// AUDIT FIX #5: Removed ambiguous FK alias 'volunteer_hours_approved_by_fkey'
// which assumed an exact PG constraint name.
// Now uses column-name disambiguation syntax: profiles!approved_by
// This works reliably when there are multiple FKs to profiles from this table.
export const getHours = async (filters?: { volunteerId?: string; status?: string }) => {
  let q = supabase.from('volunteer_hours')
    .select(`
      *,
      volunteers(id, first_name, last_name, email)
    `)
    .is('deleted_at', null)
    .order('service_date', { ascending: false })
  if (filters?.volunteerId) q = q.eq('volunteer_id', filters.volunteerId)
  if (filters?.status)      q = q.eq('status', filters.status)
  const { data, error } = await q
  if (error) throw error
  return data as VolunteerHour[]
}

export const submitHours = async (payload: {
  volunteer_id: string; service_date: string; hours: number
  activity: string; notes?: string; event_id?: string
}) => {
  const { data, error } = await supabase.from('volunteer_hours')
    .insert({ status: 'submitted', submitted_at: new Date().toISOString(), ...payload })
    .select('*, volunteers(id,first_name,last_name,email)').single()
  if (error) throw error
  return data as VolunteerHour
}

export const reviewHours = async (
  id: string,
  action: 'approved' | 'rejected',
  rejectionReason?: string
) => {
  const { data: { user } } = await supabase.auth.getUser()
  const { data, error } = await supabase.from('volunteer_hours')
    .update({
      status: action,
      approved_by: user?.id ?? null,
      reviewed_at: new Date().toISOString(),
      rejection_reason: rejectionReason ?? null,
    })
    .eq('id', id)
    .select('*, volunteers(id,first_name,last_name,email)')
    .single()
  if (error) throw error
  return data as VolunteerHour
}

export const getPendingHoursCount = async () => {
  const { count, error } = await supabase.from('volunteer_hours')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'submitted').is('deleted_at', null)
  if (error) throw error
  return count ?? 0
}

// AUDIT FIX #13: guard with try/catch — table exists in schema but may have no data
export const getVolunteerMilestones = async (volunteerId: string) => {
  try {
    const { data, error } = await supabase.from('volunteer_milestones')
      .select('*').eq('volunteer_id', volunteerId).order('achieved_at', { ascending: false })
    if (error) return []  // silently return empty on any error
    return data ?? []
  } catch {
    return []
  }
}
